import type { Row, RowSelectionState, Table } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import Link from "ui/components/Link";
import cx from "ui/cx";
import Checkbox from "ui/forms/Checkbox";
import { wrapWithTooltip } from "ui/popovers/Tooltip";
import dataTableStyles from "./DataTable.module.scss";
import styles from "./Multiselect.module.scss";
import type { DataTableRow } from "./types";

export function MultiSelectHeader<T>({
  table,
  rowSelection,
  totalCount,
  currentCount,
}: {
  table: Table<T>;
  rowSelection: RowSelectionState;
  totalCount: number;
  currentCount: number;
}) {
  const { tooltip, checkedState, onCheckedChange } = useMemo(() => {
    const selectedRowCount = Object.keys(rowSelection).length;
    let tooltip = "";
    let checkedState: boolean | "indeterminate";
    let onCheckedChange = () => table.toggleAllRowsSelected();
    if (selectedRowCount === totalCount) {
      checkedState = true;
      tooltip = "Deselect all rows";
      onCheckedChange = () => table.resetRowSelection();
    } else if (selectedRowCount) {
      checkedState = "indeterminate";
      tooltip = "Select all rows";
      if (currentCount < totalCount) {
        // Check if all rows on this page are selected
        tooltip =
          currentCount === table.getSelectedRowModel().rows.length
            ? "Deselect all rows on this page"
            : "Select all rows on this page";
      }
    } else {
      checkedState = false;
      tooltip = "Select all rows";
    }
    return { tooltip, checkedState, onCheckedChange };
  }, [rowSelection, totalCount, currentCount]);

  return wrapWithTooltip(
    <div className={cx(dataTableStyles.dummyCell, dataTableStyles.multiSelectCell)}>
      <Checkbox checked={checkedState} onCheckedChange={onCheckedChange} data-testid="select-all-checkbox" />
    </div>,
    tooltip,
  );
}

export function MultiSelectMenu<T extends {}>({
  table,
  multiSelect,
  rowSelection,
  containerRef,
  hasControls,
}: {
  table: Table<T>;
  multiSelect: DataTableRow<T>["multiSelect"];
  rowSelection: RowSelectionState; // source of truth for selected rows
  // Lets the parent measure the menu height so the sticky header can offset below it.
  containerRef?: (node: HTMLDivElement | null) => void;
  // When the table also renders a controls bar, the menu sticks below the controls instead of over them.
  hasControls?: boolean;
}) {
  const [selectedRows, setSelectedRows] = useState<Row<T>[]>([]);

  useEffect(() => {
    const { rowsById } = table.getRowModel();
    const nextSelectedRows = table.getSelectedRowModel().rows;
    // Filter all previously selected rows that are not selected or not in the current table,
    // and add the new rows to the selection
    // Keeps rows selected from different pages (for server-side pagination)
    setSelectedRows((prev) => [
      ...prev.filter((row) => rowSelection[row.id] && !rowsById[row.id]),
      ...nextSelectedRows,
    ]);
  }, [table, rowSelection]);

  if (!selectedRows.length) {
    return null;
  }
  return (
    <div
      ref={containerRef}
      className={cx(styles.multiSelectMenu, hasControls && styles.hasControls)}
      data-testid="multiselect-menu"
    >
      <Link onClick={() => table.resetRowSelection()} className={styles.rowsSelected}>
        {selectedRows.length} selected
      </Link>
      {multiSelect?.(selectedRows)}
    </div>
  );
}
